import { useMemo, useState, useEffect } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Globe, Check, Loader2 } from "lucide-react";
import { SearchableSelect } from "./SearchableSelect";
import { getPreferredTz, setPreferredTz, deviceTz } from "../lib/datetime";

function tzOptions() {
  let zones = [];
  try { zones = Intl.supportedValuesOf("timeZone"); } catch { zones = []; }
  if (!zones.length) {
    zones = ["UTC", "Africa/Harare", "Africa/Johannesburg", "Africa/Lusaka", "Africa/Nairobi",
             "Europe/London", "Europe/Paris", "America/New_York", "America/Los_Angeles", "Asia/Dubai"];
  }
  return zones.map((z) => ({ value: z, label: z.replace(/_/g, " ") }));
}

// Lets a user pick the timezone their appointment times and reminders are shown in.
// Defaults to their device zone.
export function TimezoneCard() {
  const [tz, setTz] = useState(getPreferredTz());
  const [saving, setSaving] = useState(false);
  const [now, setNow] = useState(Date.now());
  const options = useMemo(tzOptions, []);

  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000 * 30); return () => clearInterval(t); }, []);

  const save = async (val) => {
    if (!val || val === tz) return;
    setTz(val);
    setPreferredTz(val);
    setSaving(true);
    try {
      await api.put("/me/timezone", { timezone: val });
      toast.success(`Timezone updated — times now show in ${val.replace(/_/g, " ")}.`);
    } catch { toast.message("Timezone applied on this device."); }
    finally { setSaving(false); }
  };

  const nowLabel = (() => {
    try { return new Date(now).toLocaleTimeString(undefined, { timeZone: tz, hour: "numeric", minute: "2-digit", timeZoneName: "short" }); }
    catch { return ""; }
  })();

  return (
    <section data-testid="timezone-card" className="bg-white border border-line rounded-lg p-5">
      <h2 className="font-heading font-bold text-lg text-ink flex items-center gap-2 mb-1">
        <Globe className="w-5 h-5 text-clinical" /> Your timezone
      </h2>
      <p className="text-sm text-muted2 mb-3">
        Appointment times and reminders are shown in this timezone. It defaults to your device ({deviceTz().replace(/_/g, " ")}).
      </p>
      <div className="max-w-md">
        <SearchableSelect
          testid="timezone-select"
          value={tz}
          options={options}
          onChange={(v) => save(v)}
          placeholder="Select your timezone"
          searchPlaceholder="Search timezone…"
        />
      </div>
      <p className="text-xs text-muted2 mt-2 inline-flex items-center gap-1.5" data-testid="timezone-now">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 text-calm" />}
        It's currently <b className="text-ink">{nowLabel}</b> for you.
      </p>
    </section>
  );
}
